import { c as _c } from "react/compiler-runtime";
import { IcoRefresh16 } from '@onefootprint/icons';
import type { ApiOnboardingGraph, PlaybookVersionNoGraph, VaultKind } from '@onefootprint/request-types/dashboard';
import { CodeInline, Form, IconButton, JsonEditor, Tooltip } from '@onefootprint/ui';
import { Controller, useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useOrgSession from '@/hooks/use-org-session';
import useSession from '@/hooks/use-session';
import { TenantPreviewApi } from '@/types/tenant';
import { generateTestExternalId, SectionHeader } from '../../../test-form/test-form';
import type { RunFormValues } from '../../onboarding.types';
import { requiresPhoneOtp, runsVerifiedPrefill } from '../../onboarding.utils';
import { StepCard } from '../step-card/step-card';
type RunFormProps = {
  isExpanded: boolean;
  hasSavedEntity: boolean;
  hasPrerequisiteNode: boolean;
  entityKind: VaultKind;
  playbook: PlaybookVersionNoGraph;
  graph: ApiOnboardingGraph;
};
export const RunForm = t0 => {
  const $ = _c(36);
  const {
    isExpanded,
    hasSavedEntity,
    hasPrerequisiteNode,
    entityKind,
    playbook,
    graph
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "testMode"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    isLive
  } = useSession();
  const previewApis = useOrgSession().data?.allowedPreviewApis ?? [];
  let t2;
  if ($[1] !== previewApis) {
    t2 = previewApis.includes(TenantPreviewApi.ThirdPartyAuth);
    $[1] = previewApis;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hasThirdPartyAuth = t2;
  let t3;
  if ($[3] !== graph || $[4] !== hasThirdPartyAuth || $[5] !== playbook) {
    t3 = hasThirdPartyAuth && requiresPhoneOtp(playbook, graph);
    $[3] = graph;
    $[4] = hasThirdPartyAuth;
    $[5] = playbook;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const showOtp = t3;
  let t4;
  if ($[7] !== graph || $[8] !== hasThirdPartyAuth) {
    t4 = hasThirdPartyAuth && runsVerifiedPrefill(graph);
    $[7] = graph;
    $[8] = hasThirdPartyAuth;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const showConsent = t4;
  const form = useFormContext();
  let t5;
  if ($[10] !== form) {
    t5 = () => {
      form.setValue("onboardingExternalId", generateTestExternalId(), {
        shouldDirty: true
      });
    };
    $[10] = form;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleRegenerateObExternalId = t5;
  let t6;
  if ($[12] !== hasSavedEntity || $[13] !== isExpanded) {
    t6 = () => {
      if (isExpanded) {
        return "active";
      }
      return hasSavedEntity ? "ready" : "locked";
    };
    $[12] = hasSavedEntity;
    $[13] = isExpanded;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const getState = t6;
  const t7 = getState();
  let t8;
  if ($[15] !== t) {
    t8 = t(_temp);
    $[15] = t;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== entityKind || $[18] !== hasSavedEntity || $[19] !== t) {
    t9 = hasSavedEntity ? undefined : t($_0 => $_0.runMode.run.needEntityHint[entityKind]);
    $[17] = entityKind;
    $[18] = hasSavedEntity;
    $[19] = t;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== form || $[22] !== handleRegenerateObExternalId || $[23] !== hasPrerequisiteNode || $[24] !== isExpanded || $[25] !== isLive || $[26] !== showConsent || $[27] !== showOtp || $[28] !== t) {
    t10 = isExpanded ? <><SectionHeader size="compact" title={t(_temp2)} description={t(_temp3)} /><Form.Input name="onboardingExternalId" placeholder="abcde12345" size="compact" className="pr-9!" suffixComponent={<Tooltip content={t(_temp4)}><IconButton aria-label={t(_temp5)} icon={IcoRefresh16} onClick={handleRegenerateObExternalId} size="tiny" /></Tooltip>} />{!isLive && <><SectionHeader size="compact" title={t(_temp6)} description={t(_temp7)} /><Form.Select name="sandboxOutcome" size="compact" options={[{
          value: "pass",
          label: t(_temp8)
        }, {
          value: "fail",
          label: t(_temp9)
        }, {
          value: "manual_review",
          label: t(_temp10)
        }, {
          value: "evaluate_rules",
          label: t(_temp11)
        }, {
          value: "choose_branch_outcomes",
          label: t(_temp12)
        }]} /></>}<SectionHeader size="compact" title={t(_temp13)} description={t(_temp14)} /><Form.Number name="synchronousTimeoutSecs" size="compact" aria-label={t(_temp15)} placeholder={t(_temp16)} decimalPlaces={0} allowNegative={false} thousandsSeparator={false} rules={{
        max: {
          value: 30,
          message: t(_temp17)
        }
      }} />{hasPrerequisiteNode && <><SectionHeader size="compact" title={t(_temp18)} description={<Trans ns="playbook-builder" i18nKey={_temp19} components={{
          codeInline: <CodeInline size="compact" disabled={true}>{"data."}</CodeInline>
        }} />} /><Controller control={form.control} name="prerequisiteData" render={t11 => {
          const {
            field,
            fieldState
          } = t11;
          return <div className="flex flex-col gap-2"><JsonEditor value={field.value} onChange={field.onChange} onBlur={field.onBlur} size="medium" allowTrailingCommas={true} onValidate={markers => markers.length > 0 ? form.setError("prerequisiteData", {
              type: "validation",
              message: markers[0].message
            }) : form.clearErrors("prerequisiteData")} />{fieldState.error?.message && <p className="text-body-3 text-error">{fieldState.error.message}</p>}</div>;
        }} /></>}{(showOtp || showConsent) && <><SectionHeader size="compact" title={t(_temp20)} description={t(_temp21)} />{showOtp && <Form.Toggle name="thirdPartyAuth" label={t(_temp22)} />}{showConsent && <Form.Toggle name="verifiedPrefillConsent" label={t(_temp23)} />}</>}</> : null;
    $[21] = form;
    $[22] = handleRegenerateObExternalId;
    $[23] = hasPrerequisiteNode;
    $[24] = isExpanded;
    $[25] = isLive;
    $[26] = showConsent;
    $[27] = showOtp;
    $[28] = t;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== isExpanded || $[31] !== t10 || $[32] !== t7 || $[33] !== t8 || $[34] !== t9) {
    t11 = <StepCard step={2} state={t7} title={t8} hint={t9} focusOnExpand={isExpanded}>{t10}</StepCard>;
    $[30] = isExpanded;
    $[31] = t10;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
function _temp($) {
  return $.runMode.run.title;
}
function _temp2($_1) {
  return $_1.runMode.run.obExternalId;
}
function _temp3($_2) {
  return $_2.runMode.run.obExternalIdHint;
}
function _temp4($_3) {
  return $_3.runMode.run.regenerate;
}
function _temp5($_4) {
  return $_4.runMode.run.regenerate;
}
function _temp6($_5) {
  return $_5.testForm.fields.sandboxOutcome;
}
function _temp7($_6) {
  return $_6.testForm.documentation.sandboxOutcome;
}
function _temp8($_7) {
  return $_7.testForm.documentation.sandboxOutcomes.pass;
}
function _temp9($_8) {
  return $_8.testForm.documentation.sandboxOutcomes.fail;
}
function _temp10($_9) {
  return $_9.testForm.documentation.sandboxOutcomes.manualReview;
}
function _temp11($_10) {
  return $_10.testForm.documentation.sandboxOutcomes.evaluateRules;
}
function _temp12($_11) {
  return $_11.testForm.documentation.sandboxOutcomes.chooseBranchOutcomes;
}
function _temp13($_12) {
  return $_12.runMode.run.syncTimeout;
}
function _temp14($_13) {
  return $_13.runMode.run.syncTimeoutHint;
}
function _temp15($_14) {
  return $_14.runMode.run.syncTimeout;
}
function _temp16($_15) {
  return $_15.runMode.run.syncTimeoutPlaceholder;
}
function _temp17($_16) {
  return $_16.runMode.run.syncTimeoutMax;
}
function _temp18($_17) {
  return $_17.runMode.run.prerequisiteData;
}
function _temp19($_18) {
  return $_18.testMode.runMode.run.prerequisiteDataHint;
}
function _temp20($_19) {
  return $_19.runMode.run.attestations;
}
function _temp21($_20) {
  return $_20.runMode.run.attestationsHint;
}
function _temp22($_21) {
  return $_21.runMode.run.thirdPartyAuthLabel;
}
function _temp23($_22) {
  return $_22.runMode.run.verifiedPrefillConsentLabel;
}